import { useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { formatDate } from '@utils/format';
import { useUpdateForecast } from '../hooks';
import {
  forecastFormSchema,
  type Forecast,
  type ForecastFormValues,
} from '../schema';

interface Props {
  open: boolean;
  row: Forecast | null;
  onClose: () => void;
}

function buildDefaults(row: Forecast | null): ForecastFormValues {
  return {
    isActive: row?.isActive ?? true,
    notes: row?.notes ?? '',
    predictedMin: row?.predictedMin ?? null,
    predictedMax: row?.predictedMax ?? null,
  };
}

function formatRupees(v: number | null): string {
  if (v == null) return '—';
  return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export function ForecastEditDialog({ open, row, onClose }: Props) {
  const updateMutation = useUpdateForecast();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ForecastFormValues>({
    resolver: zodResolver(forecastFormSchema) as Resolver<ForecastFormValues>,
    defaultValues: buildDefaults(null),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !row) return;
    reset(buildDefaults(row));
  }, [open, row, reset]);

  const submitting = isSubmitting || updateMutation.isPending;

  const onSubmit = async (values: ForecastFormValues) => {
    if (!row) return;
    try {
      await updateMutation.mutateAsync({
        id: row.id,
        payload: {
          isActive: values.isActive,
          notes: values.notes,
          predictedMin: values.predictedMin,
          predictedMax: values.predictedMax,
        },
      });
      onClose();
    } catch {
      // Toast already shown by the mutation's onError.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="forecast-edit-title"
    >
      <DialogTitle id="forecast-edit-title" sx={{ pb: 0.5 }}>
        Edit forecast
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-lg bg-surface-muted px-4 py-3 text-[12.5px]">
              <div className="col-span-2">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Series
                </div>
                <div className="font-medium text-foreground">
                  {row.cropName} · {row.varietyName}
                </div>
                <div className="text-foreground-muted">{row.apmcName}</div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Metric · Horizon
                </div>
                <div className="font-medium text-foreground">
                  {row.metric.toUpperCase()} · {row.horizonDays}d
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Predicted on → Target
                </div>
                <div className="font-medium text-foreground">
                  {formatDate(row.predictionDay)} → {formatDate(row.targetDate)}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Price at run
                </div>
                <div className="font-medium tabular-nums text-foreground">
                  ₹{formatRupees(row.currentCropPrice)}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Model
                </div>
                <div className="font-mono text-[12px] text-foreground">{row.modelName}</div>
              </div>
            </div>

            <form
              id="forecast-edit-form"
              onSubmit={handleSubmit(onSubmit)}
              className="grid gap-3"
            >
              <div className="rounded-lg border border-border bg-surface-muted/40 px-3 py-2.5">
                <div className="mb-2 text-[13.5px] font-semibold text-foreground">
                  Predicted range
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Controller
                    control={control}
                    name="predictedMin"
                    render={({ field }) => (
                      <TextField
                        label="Min (₹)"
                        size="small"
                        type="number"
                        fullWidth
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? null : Number(e.target.value),
                          )
                        }
                        onBlur={field.onBlur}
                        error={!!errors.predictedMin}
                        helperText={errors.predictedMin?.message ?? ' '}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="predictedMax"
                    render={({ field }) => (
                      <TextField
                        label="Max (₹)"
                        size="small"
                        type="number"
                        fullWidth
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === '' ? null : Number(e.target.value),
                          )
                        }
                        onBlur={field.onBlur}
                        error={!!errors.predictedMax}
                        helperText={errors.predictedMax?.message ?? ' '}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    )}
                  />
                </div>
                <p className="text-[12px] leading-snug text-foreground-muted">
                  Override the model's predicted band — e.g. to correct an obviously
                  bad output. Leave a field blank to clear that bound.
                </p>
              </div>

              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <div className="rounded-lg border border-border bg-surface-muted/40 px-3 py-2">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <span className="text-[13.5px] font-semibold text-foreground">
                          Active
                        </span>
                      }
                      sx={{ ml: -1, mb: 0 }}
                    />
                    <p className="mt-0.5 pl-9 text-[12px] leading-snug text-foreground-muted">
                      When off: this forecast is hidden in the consumer app and excluded from accuracy reporting. Use to suppress obviously bad model output.
                    </p>
                  </div>
                )}
              />

              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={3}
                placeholder='e.g. "30-day max capped from 12500 to 11000 because current=10500 (capping triggered)"'
                error={!!errors.notes}
                helperText={
                  errors.notes?.message ??
                  'Document capping decisions or override reasoning. 2000 chars max.'
                }
                {...register('notes')}
              />
            </form>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="forecast-edit-form"
          variant="contained"
          color="primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
